import { BackCoverFooter, PageShell, TopNav } from "@/app/components/brand-scaffold";
import { Collapsible } from "@/app/components/ui/collapsible";
import { FaqSection } from "@/app/components/seo-support";
import { siteHeaderActions, siteNavItems } from "@/app/content";
import { ROUTE_FAQS } from "@/app/seo-content";
import { CodeBlock, NextCta, Note, QuickStartHero, Step } from "./QuickStartShared";

const SAMPLE_RESULT = `{
  "request_id": "req_xxx",
  "status": "SUCCEEDED",
  "result_package": {
    "request_id": "req_xxx",
    "responder_id": "responder_xxx",
    "hotline_id": "local.delegated-execution.workspace-summary.v1",
    "status": "ok",
    "output": {
      "summary": "..."
    },
    "timing": {
      "elapsed_ms": 123
    }
  }
}`;

export default function QuickStartCaller() {
  return (
    <PageShell>
      <TopNav navItems={siteNavItems} actionItems={siteHeaderActions} homeHref="/" />

      <QuickStartHero
        role="Caller · 5 min"
        roleColor="#A3E635"
        titleTop="把外部能力"
        titleBottom="接进 AGENT"
        lead="先用已发布的 delexec-ops 包跑一次本地 hotline，看一份 result_package，理解协议怎么帮你省掉「每根 API 单独拼接」的工。源码拆解路径放在后面。"
        meta={["Local Mode", "无需 Platform", "走 supervisor HTTP"]}
      />

      <main className="relative z-10 mx-auto w-full max-w-5xl px-4 py-10 sm:px-6 lg:px-8 lg:py-16">
        <div className="mb-8 border-4 border-black bg-white/85 backdrop-blur-sm p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-zinc-500">这页教你做什么</p>
          <h2 className="mt-2 text-xl font-black text-black sm:text-2xl">
            从 0 到「Agent 真的发起了一次跨进程调用」
          </h2>
          <ul className="mt-4 grid gap-2 text-sm font-bold text-zinc-800 sm:grid-cols-2">
            <li>· 启动 Client 本地 supervisor</li>
            <li>· 用 CLI 注册一个本地 Caller 身份</li>
            <li>· 把 example hotline 挂上来当作 Responder 模拟</li>
            <li>· 用 run-example 发起 example call · 拉 result</li>
            <li>· 解读 result_package：Agent 拿到的协议化结果长什么样</li>
          </ul>
        </div>

        <div className="space-y-8">
          <Step
            num="01"
            title="官方路径：安装 CLI + 跑通 bootstrap"
            intent="陌生用户从已发布的 @delexec/ops 开始，不需要 clone 第四仓库，也不需要先理解三仓结构。"
          >
            <CodeBlock language="bash" caption="任意目录">{`npm install -g @delexec/ops
delexec-ops bootstrap --email you@example.com`}</CodeBlock>

            <Note>
              这两条命令会安装 CLI 并启动本地 runtime。后续用
              <code className="font-mono"> delexec-ops run-example </code>
              即可验证 Caller 最小闭环。
            </Note>

            <Collapsible title="从源码运行 / Contributor path">
              <div className="space-y-2 text-sm text-zinc-600">
                <p>如果你需要修改 CLI 源码或参与贡献：</p>
                <pre className="bg-zinc-100 p-3 rounded text-xs overflow-x-auto">
{`git clone --recursive https://github.com/hejiajiudeeyu/delegated-execution-dev.git
cd delegated-execution-dev
corepack pnpm install

cd repos/client
npm install`}
                </pre>
              </div>
            </Collapsible>
          </Step>

          <Step
            num="02"
            title="源码拆解：启动 supervisor"
            intent="如果你在贡献代码或排查 HTTP 细节，可以从源码启动 supervisor。普通首次体验只需要上面的 delexec-ops bootstrap。"
          >
            <CodeBlock language="bash" caption="repos/client">{`# 可选：把本机状态隔离到一个独立目录，避免污染默认 ~/.delexec
export DELEXEC_HOME="$HOME/.delexec-quickstart"
export OPS_PORT_SUPERVISOR=8179

DELEXEC_HOME="$DELEXEC_HOME" \\
OPS_PORT_SUPERVISOR="$OPS_PORT_SUPERVISOR" \\
npm run ops -- start`}</CodeBlock>

            <Note variant="warn">
              这条命令会前台 running，建议另起一个终端做后续步骤。看到
              <code className="font-mono"> supervisor listening on 127.0.0.1:8179 </code>
              即 OK。
            </Note>
          </Step>

          <Step
            num="03"
            title="初始化并取一个会话 token"
            intent="supervisor 第一次跑要做一次 setup，之后用 X-Ops-Session 标识当前管理员会话。"
          >
            <CodeBlock language="bash">{`BASE="http://127.0.0.1:\${OPS_PORT_SUPERVISOR:-8079}"
# 第一次跑：初始化 + 拿 session
curl -s -X POST "$BASE/setup" -H 'content-type: application/json' -d '{}'
SESSION=$(curl -s -X POST "$BASE/auth/session/setup" \\
  -H 'content-type: application/json' \\
  -d '{"passphrase":"client-localtest-123"}' | jq -r '.token')
echo "$SESSION"
export OPS_SESSION="$SESSION"`}</CodeBlock>

            <Note>
              想跳过 curl？后面的步骤也都有 CLI 等价命令；详见
              <code className="font-mono"> repos/client/docs/current/guides/local-mode-onboarding.md</code>。
            </Note>
          </Step>

          <Step
            num="04"
            title="注册一个本地 Caller 身份"
            intent="即使是 Local Mode，protocol 也要求 Caller 有明确身份；这一步会在 DELEXEC_HOME 下生成本地账号。"
          >
            <CodeBlock language="bash" caption="CLI 写法">{`npm run ops -- auth register --local --email you@example.com`}</CodeBlock>

            <CodeBlock language="bash" caption="HTTP 写法">{`curl -s -X POST "$BASE/auth/register-caller" \\
  -H 'content-type: application/json' \\
  -H "X-Ops-Session: $OPS_SESSION" \\
  -d '{ "contact_email": "you@example.com" }'`}</CodeBlock>
          </Step>

          <Step
            num="05"
            title="挂一根「示例 hotline」上来"
            intent="Local Mode 下，让本机同时扮演 Responder，挂一条用于联调的 workspace-summary 示例 hotline。生产场景里这一步是从 catalog 拉到的真实能力。"
          >
            <CodeBlock language="bash">{`# 启用 responder 角色
curl -s -X POST "$BASE/responder/enable" \\
  -H 'content-type: application/json' \\
  -H "X-Ops-Session: $OPS_SESSION" \\
  -d '{}'

# 挂上 example hotline（本仓内置）
curl -s -X POST "$BASE/responder/hotlines/example" \\
  -H 'content-type: application/json' \\
  -H "X-Ops-Session: $OPS_SESSION" \\
  -d '{}'`}</CodeBlock>
          </Step>

          <Step
            num="06"
            title="列出本机能调用的 hotline"
            intent="这是 Caller 视角里最常用的 API：catalog 决定了 Agent 当前可调用的能力集合。"
          >
            <CodeBlock language="bash">{`curl -s "$BASE/catalog/hotlines" \\
  -H "X-Ops-Session: $OPS_SESSION" | jq`}</CodeBlock>

            <Note>
              你应当看到一条 <code className="font-mono">local.delegated-execution.workspace-summary.v1</code>，
              这是 Agent 接下来要调的 hotline。
            </Note>
          </Step>

          <Step
            num="07"
            title="发起一次真实调用"
            intent="一切准备好了。下面这条命令模拟 Agent 在 Caller 进程里发起一个 hotline 调用。"
          >
            <CodeBlock language="bash" caption="HTTP 写法">{`REQ=$(curl -s -X POST "$BASE/requests/example" \\
  -H 'content-type: application/json' \\
  -H "X-Ops-Session: $OPS_SESSION" \\
  -d '{}')
echo "$REQ"
REQUEST_ID=$(echo "$REQ" | jq -r '.request_id')`}</CodeBlock>

            <CodeBlock language="bash" caption="CLI 写法">{`npm run ops -- run-example --text "summarize my workspace"`}</CodeBlock>
          </Step>

          <Step
            num="08"
            title="拉结果 · 看 result_package"
            intent="协议化结果是 CALL ANYTHING 的核心：所有 hotline 返回同一种 result_package 结构，Agent 只学一次。"
          >
            <CodeBlock language="bash">{`curl -s "$BASE/requests/$REQUEST_ID/result" \\
  -H "X-Ops-Session: $OPS_SESSION" | jq`}</CodeBlock>

            <CodeBlock language="json" caption="脱敏后的真实形状">{SAMPLE_RESULT}</CodeBlock>

            <div className="border-2 border-black bg-white/80 backdrop-blur-sm p-4 text-sm font-bold text-zinc-800">
              <p>5 个字段 Agent 必看：</p>
              <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
                <li>· <code className="font-mono">status</code>：SUCCEEDED / FAILED</li>
                <li>· <code className="font-mono">result_package.status</code>：ok / error</li>
                <li>· <code className="font-mono">hotline_id</code>：调用的能力 ID（含版本）</li>
                <li>· <code className="font-mono">output</code>：具体输出，shape 由该 hotline 的 output_schema 决定</li>
                <li>· <code className="font-mono">timing.elapsed_ms</code>：耗时，可作为计费 / 评分输入</li>
              </ul>
            </div>
          </Step>

          <Step
            num="09"
            title="OPC #0 生产演练：充值后用一条 CLI 发起一次付费调用"
            intent="这不是本地 quick start 的必做步骤；它用于公开生产演练。Caller 先注册公开 Platform 身份，把 tenant_id 交给 operator 充值，然后直接用 delexec-ops call-hotline 完成 billing consent、dispatch、result polling 和结算核对。"
          >
            <CodeBlock language="bash" caption="注册 Caller · 配置 public relay runtime · 交给 operator 充值">{`PLATFORM="https://callanything.xyz/platform"
RELAY="https://callanything.xyz/relay"
CALLER_EMAIL="caller-opc0"@"example.com"
export DELEXEC_HOME="$HOME/.delexec-caller-public"
export OPS_PORT_SUPERVISOR=8279
export OPS_PORT_CALLER=8281
export TRANSPORT_TYPE="relay_http"
export TRANSPORT_BASE_URL="$RELAY"

# delexec-ops auth register 会调用 $PLATFORM/v1/users/register 并把 API key 写入本地状态。
CALLER_JSON=$(delexec-ops auth register \\
  --platform "$PLATFORM" \\
  --email "$CALLER_EMAIL")
echo "$CALLER_JSON" | jq

CALLER_API_KEY=$(echo "$CALLER_JSON" | jq -r '.api_key')
CALLER_TENANT_ID=$(echo "$CALLER_JSON" | jq -r '.user_id')
export CALLER_API_KEY CALLER_TENANT_ID

# 另起终端前台运行，或用 nohup 长运行；Caller controller 会消费 result 并上报结算事件。
nohup env \\
  DELEXEC_HOME="$DELEXEC_HOME" \\
  OPS_PORT_SUPERVISOR="$OPS_PORT_SUPERVISOR" \\
  OPS_PORT_CALLER="$OPS_PORT_CALLER" \\
  TRANSPORT_TYPE="relay_http" \\
  TRANSPORT_BASE_URL="$RELAY" \\
  delexec-ops start \\
  > "$DELEXEC_HOME/caller-public-relay.log" 2>&1 &

CALLER_BASE="http://127.0.0.1:$OPS_PORT_CALLER"

# 把 CALLER_TENANT_ID 发给 operator。operator 完成 tenant 创建和人工充值后再继续。
curl -s "$PLATFORM/v1/tenants/me/balance" \\
  -H "Authorization: Bearer $CALLER_API_KEY" | jq`}</CodeBlock>

            <CodeBlock language="bash" caption="选择已审核 hotline · 一条 CLI 完成付费调用">{`HOTLINE_ID="your.namespace.tool-name.v1"
RESPONDER_ID="responder_xxx"
REQUEST_ID="req_opc0_$(date +%s)"
MAX_CHARGE_CENTS=500

curl -s "$PLATFORM/v1/catalog/hotlines/$HOTLINE_ID" | jq

CALL_RESULT=$(delexec-ops call-hotline \\
  --platform "$PLATFORM" \\
  --caller-base-url "$CALLER_BASE" \\
  --request-id "$REQUEST_ID" \\
  --responder-id "$RESPONDER_ID" \\
  --hotline-id "$HOTLINE_ID" \\
  --text "Summarize this paid public relay request." \\
  --max-charge-cents "$MAX_CHARGE_CENTS" \\
  --poll-interval-ms 2000 \\
  --timeout-ms 60000)

echo "$CALL_RESULT" | tee /tmp/delexec-call-hotline.json | jq
echo "$CALL_RESULT" | jq '{request_id, task_token_present, task_token_claims, delivery_meta, dispatch, inbox, result, events, balance, ledger}'`}</CodeBlock>

            <CodeBlock language="bash" caption="诊断时再看底层形状">{`# 上面的 call-hotline 已经封装了这些步骤：
# /v1/tokens/task
# /v1/requests/$REQUEST_ID/delivery-meta
# /controller/requests
# /controller/requests/$REQUEST_ID/dispatch
# $RELAY/v1/messages/send
# /controller/inbox/pull
# /controller/requests/$REQUEST_ID/result
# /v1/tenants/me/balance
# /v1/tenants/me/ledger
# /v1/requests/$REQUEST_ID/events
echo "$CALL_RESULT" | jq '{task_token_claims, delivery_meta, dispatch, inbox, result, events, balance, ledger}'`}</CodeBlock>

            <Note variant="warn">
              付费调用的验收看三件事：<code className="font-mono">call-hotline</code> 返回 0 且带出
              <code className="font-mono">task_token_claims</code>、<code className="font-mono">delivery_meta</code>、
              <code className="font-mono">dispatch</code>、<code className="font-mono">result</code>、<code className="font-mono">events</code>、
              <code className="font-mono">balance</code>、<code className="font-mono">ledger</code>；<code className="font-mono">/v1/tenants/me/balance</code> 和
              <code className="font-mono">/v1/tenants/me/ledger</code> 的金额变化与
              <code className="font-mono">max_charge_cents</code>、实际 usage 一致。底层 API 仍保留给排障时核对。
            </Note>
          </Step>
        </div>

        {/* What's next */}
        <div className="mt-12 border-[6px] border-black bg-[#A3E635]/85 backdrop-blur-md p-6 sm:p-8 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]">
          <h2 className="text-2xl font-black uppercase text-black sm:text-3xl">下一步</h2>
          <p className="mt-3 text-sm font-bold text-zinc-900 sm:text-base">
            你刚刚跑通的就是 Caller 的最小闭环。从这里可以分两条路深入：
          </p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="border-4 border-black bg-white/90 backdrop-blur-sm p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-zinc-500">扩展 Agent 能力</p>
              <p className="mt-2 text-sm font-bold text-black">
                打开 Console，从本地 catalog 浏览更多 hotline，按风险标签配置审批策略。
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <NextCta href="/console" label="进 Console 原型" />
                <NextCta href="/marketplace" label="逛 Marketplace" tone="white" />
              </div>
            </div>
            <div className="border-4 border-black bg-white/90 backdrop-blur-sm p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-zinc-500">自己也想发能力？</p>
              <p className="mt-2 text-sm font-bold text-black">
                同一台 supervisor 可以同时做 Caller 和 Responder。换条路看 Responder 怎么 5 分钟发布一根 hotline。
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <NextCta href="/docs/quick-start-responder" label="进 Responder Quick Start" tone="blue" />
                <NextCta href="/docs" label="完整文档" tone="white" />
              </div>
            </div>
          </div>
        </div>

        <FaqSection
          eyebrow="Caller FAQ"
          title="Caller Quick Start 常见问题"
          intro="这部分直接回答第一次做 Caller 接入时最容易卡住的几个问题，也对应页面里的 HowTo schema。"
          items={ROUTE_FAQS["/docs/quick-start-caller/"]}
          accent="#A3E635"
        />
      </main>

      <BackCoverFooter />
    </PageShell>
  );
}
